// XLSX helpers for bulk blind-box distribution (exceljs).
// The upstream Admin BE does not (yet) own file parsing for this endpoint, so we parse client-side
// and feed the rows into the existing batch-grant flow.
//
// Box type, reason, and notes are chosen once in the form and apply to every row, so the sheet only
// carries the per-recipient columns:
//   - NORMAL:            walletAddress, quantity
//   - DESIGNATED_AMOUNT: walletAddress, amount
import { Workbook, Worksheet } from 'exceljs';
import type { BbBoxType } from '../../../services/blindBoxService';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** Every column the parser understands; a given template uses a subset (see columnsForType). */
export const MD_SHEET_COLUMNS = ['walletAddress', 'quantity', 'amount'] as const;

export type MdSheetColumn = (typeof MD_SHEET_COLUMNS)[number];

/** Columns present in the template / expected in the upload, per selected box type.
 *  DESIGNATED_AMOUNT is always one box per row, so it carries no quantity column. */
export function columnsForType(boxType: BbBoxType): MdSheetColumn[] {
  return boxType === 'DESIGNATED_AMOUNT'
    ? ['walletAddress', 'amount']
    : ['walletAddress', 'quantity'];
}

const EXAMPLE_WALLET_A = '0x1234567890abcdef1234567890abcdef12345678';
const EXAMPLE_WALLET_B = '0xabcdefabcdefabcdefabcdefabcdefabcdefabcd';

// ── Download plumbing ─────────────────────────────────────────────────────────

function triggerDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** Build a one-sheet workbook (bold header, roomy columns) and download it as .xlsx. */
async function downloadWorkbook(
  filename: string,
  headers: string[],
  rows: string[][]
): Promise<void> {
  const wb = new Workbook();
  const ws = wb.addWorksheet('Sheet1');
  ws.addRow(headers);
  ws.getRow(1).font = { bold: true };
  rows.forEach((r) => ws.addRow(r));
  ws.columns.forEach((col) => {
    col.width = 28;
  });
  const buffer = await wb.xlsx.writeBuffer();
  triggerDownload(filename, new Blob([buffer], { type: XLSX_MIME }));
}

/** Download the .xlsx template for the selected box type (header + two worked example rows). */
export async function downloadTemplate(boxType: BbBoxType): Promise<void> {
  const cols = columnsForType(boxType);
  const examples: Record<MdSheetColumn, string>[] =
    boxType === 'DESIGNATED_AMOUNT'
      ? [
          // One box per row; add another row (same wallet) to grant several designated boxes.
          { walletAddress: EXAMPLE_WALLET_A, quantity: '1', amount: '50' },
          { walletAddress: EXAMPLE_WALLET_B, quantity: '1', amount: '100' },
        ]
      : [
          { walletAddress: EXAMPLE_WALLET_A, quantity: '3', amount: '' },
          { walletAddress: EXAMPLE_WALLET_B, quantity: '1', amount: '' },
        ];
  const suffix = boxType === 'DESIGNATED_AMOUNT' ? 'designated-amount' : 'normal';
  await downloadWorkbook(
    `blind-box-manual-distribution-${suffix}-template.xlsx`,
    cols,
    examples.map((ex) => cols.map((c) => ex[c]))
  );
}

/** Download an arbitrary header + rows table as .xlsx (used by the records export). */
export async function exportRowsXlsx(
  filename: string,
  headers: string[],
  rows: string[][]
): Promise<void> {
  await downloadWorkbook(filename, headers, rows);
}

// ── Parsing (import) ──────────────────────────────────────────────────────────

/** exceljs cell value → trimmed string (handles numbers, dates, rich text, formulas, hyperlinks). */
function cellToString(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (v instanceof Date) return v.toISOString();
  const o = v as {
    text?: unknown;
    richText?: { text?: unknown }[];
    result?: unknown;
    hyperlink?: unknown;
  };
  if (typeof o.text === 'string') return o.text.trim();
  if (Array.isArray(o.richText)) return o.richText.map((r) => String(r?.text ?? '')).join('').trim();
  if (o.result !== undefined && o.result !== null) return cellToString(o.result);
  if (o.hyperlink) return String(o.hyperlink).trim();
  return String(v).trim();
}

const isBlankRow = (cols: string[]) => cols.every((c) => c.trim() === '');

/** Read the first worksheet into a dense string matrix (row.values is 1-indexed in exceljs). */
async function readSheetRows(data: ArrayBuffer): Promise<string[][]> {
  const wb = new Workbook();
  type LoadArg = Parameters<typeof wb.xlsx.load>[0];
  await wb.xlsx.load(data as unknown as LoadArg);
  const ws: Worksheet | undefined = wb.worksheets[0];
  if (!ws) return [];
  const rows: string[][] = [];
  ws.eachRow({ includeEmpty: false }, (row) => {
    const values = Array.isArray(row.values) ? (row.values as unknown[]) : [];
    const cells: string[] = [];
    for (let i = 1; i < values.length; i += 1) cells.push(cellToString(values[i]));
    rows.push(cells);
  });
  return rows;
}

/**
 * Parse the sheet into records keyed by the canonical column names. The first non-blank row is the
 * header; unknown columns are ignored and missing ones default to ''. Header matching is
 * case-insensitive, so `amount` is simply absent for a NORMAL template.
 */
export async function parseSheetRecords(
  data: ArrayBuffer
): Promise<Record<MdSheetColumn, string>[]> {
  const table = (await readSheetRows(data)).filter((cols) => !isBlankRow(cols));
  if (table.length < 2) return [];

  const header = table[0].map((h) => h.trim().toLowerCase());
  const colIndex = {} as Record<MdSheetColumn, number>;
  MD_SHEET_COLUMNS.forEach((col) => {
    colIndex[col] = header.indexOf(col.toLowerCase());
  });

  return table.slice(1).map((cols) => {
    const rec = {} as Record<MdSheetColumn, string>;
    MD_SHEET_COLUMNS.forEach((col) => {
      const idx = colIndex[col];
      rec[col] = idx >= 0 ? (cols[idx] ?? '').trim() : '';
    });
    return rec;
  });
}
